import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format, parse, addMonths, subMonths } from "date-fns";
import { es } from "date-fns/locale";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { PAGE, CARD, BTN_PRIMARY, BTN_GHOST, INPUT, ICON_BTN } from "@/lib/styles";
import { Wallet, TrendingUp, TrendingDown, Plus, ChevronLeft, ChevronRight, Trash2, Target, X, Loader2, PiggyBank, AlertTriangle, Settings2, Tag } from "lucide-react";
import { HelpButton } from "@/components/HelpButton";
import { toast } from "sonner";

const PALETTE = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#0ea5e9", "#a855f7", "#14b8a6", "#f43f5e", "#eab308", "#8b5cf6", "#22c55e"];
const bs = new Intl.NumberFormat("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usd = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
const CUR_SYMBOL = { VES: "Bs.", USD: "$", EUR: "€", USDT: "USDT", USDC: "USDC" };

export default function Finanzas() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [month, setMonth] = useState(format(new Date(), "yyyy-MM"));
  const [scope, setScope] = useState("__all__");
  const [third, setThird] = useState("USDT");
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txModal, setTxModal] = useState(false);
  const [budgetModal, setBudgetModal] = useState(false);
  const [scopeModal, setScopeModal] = useState(false);

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/meta");
      setMeta(data);
      setThird(data.third_currency || "USDT");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([
        api.get("/finance/summary", { params: { month, scope, third } }),
        api.get("/finance/transactions", { params: { month, scope } }),
      ]);
      setSummary(s.data);
      setTxs(t.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [month, scope, third]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadData(); }, [loadData]);

  const monthLabel = useMemo(() => format(parse(month, "yyyy-MM", new Date()), "MMMM yyyy", { locale: es }), [month]);
  const shiftMonth = (dir) => {
    const d = parse(month, "yyyy-MM", new Date());
    setMonth(format(dir > 0 ? addMonths(d, 1) : subMonths(d, 1), "yyyy-MM"));
  };

  const saveScopes = async (nextScopes) => {
    // dedupe (case-insensitive) preserving order and trim
    const seen = new Set();
    const cleaned = [];
    for (const s of nextScopes) {
      const v = String(s || "").trim();
      if (!v) continue;
      const key = v.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      cleaned.push(v);
    }
    if (cleaned.length === 0) { toast.error("Debe quedar al menos un ámbito"); return; }
    try {
      const { data } = await api.put("/finance/settings", { scopes: cleaned });
      setMeta((m) => ({ ...m, scopes: data.scopes }));
      if (scope !== "__all__" && !data.scopes.includes(scope)) setScope("__all__");
      return data.scopes;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const changeThird = async (v) => {
    setThird(v);
    try { await api.put("/finance/settings", { third_currency: v }); } catch { /* noop */ }
  };

  const deleteTx = async (id) => {
    try { await api.delete(`/finance/transactions/${id}`); toast.success("Movimiento eliminado"); loadData(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteBudget = async (id) => {
    try { await api.delete(`/finance/budgets/${id}`); toast.success("Presupuesto eliminado"); loadData(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const t = summary?.totals;
  const thirdSym = CUR_SYMBOL[third] || third;

  const Money = ({ v, sym = "Bs.", fmt = bs, cls = "" }) => (
    <span className={cls}>{sym} {fmt.format(v || 0)}</span>
  );

  return (
    <div className={`${PAGE} font-body`}>
      <Navbar />
      <div className="max-w-6xl mx-auto p-5 md:p-8" data-testid="finanzas-page">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-600/10 text-emerald-500 flex items-center justify-center"><PiggyBank size={26} /></div>
            <div>
              <h1 className="font-heading font-light text-4xl sm:text-5xl tracking-tight flex items-center gap-2">Gastos <HelpButton tool="finance" /></h1>
              <p className="font-mono2 text-xs text-slate-500 dark:text-slate-400 mt-1">Ingresos, gastos y presupuestos</p>
            </div>
          </div>
          <button onClick={() => setTxModal(true)} className={BTN_PRIMARY} data-testid="add-tx-button"><Plus size={16} /> Añadir movimiento</button>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex items-center gap-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-1 py-1">
            <button onClick={() => shiftMonth(-1)} className={ICON_BTN} data-testid="month-prev"><ChevronLeft size={16} /></button>
            <span className="px-2 font-mono2 text-sm capitalize min-w-[130px] text-center" data-testid="month-label">{monthLabel}</span>
            <button onClick={() => shiftMonth(1)} className={ICON_BTN} data-testid="month-next"><ChevronRight size={16} /></button>
          </div>
          <div className="flex items-center gap-2">
            <select value={scope} onChange={(e) => setScope(e.target.value)} className={`${INPUT} py-2 w-auto`} data-testid="scope-select">
              <option value="__all__">Todos los ámbitos</option>
              {meta?.scopes?.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={() => setScopeModal(true)} className={ICON_BTN} title="Gestionar ámbitos" data-testid="add-scope"><Settings2 size={16} /></button>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono2 text-[11px] text-slate-400">3ª moneda:</span>
            <select value={third} onChange={(e) => changeThird(e.target.value)} className={`${INPUT} py-2 w-auto`} data-testid="third-select">
              {["USDT", "USDC", "EUR", "USD"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="py-24 flex justify-center text-slate-400"><Loader2 className="animate-spin" size={26} /></div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <SummaryCard label="Ingresos" Icon={TrendingUp} color="text-emerald-500 bg-emerald-500/10" tone="text-emerald-600 dark:text-emerald-400" data={t?.income} thirdSym={thirdSym} testid="card-income" />
              <SummaryCard label="Gastos" Icon={TrendingDown} color="text-rose-500 bg-rose-500/10" tone="text-rose-600 dark:text-rose-400" data={t?.expense} thirdSym={thirdSym} testid="card-expense" />
              <SummaryCard label="Balance" Icon={Wallet} color="text-indigo-500 bg-indigo-500/10" tone={(t?.balance?.ves ?? 0) >= 0 ? "text-indigo-600 dark:text-indigo-400" : "text-rose-600 dark:text-rose-400"} data={t?.balance} thirdSym={thirdSym} testid="card-balance" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Donut by category */}
              <div className={`${CARD} p-5`}>
                <h3 className="font-heading font-medium text-base mb-3">Gastos por categoría</h3>
                {summary?.by_category?.length ? (
                  <div className="flex items-center gap-4">
                    <div className="w-40 h-40 shrink-0" data-testid="category-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={summary.by_category} dataKey="ves" nameKey="category" innerRadius={45} outerRadius={72} paddingAngle={2}>
                            {summary.by_category.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                          </Pie>
                          <Tooltip formatter={(v) => `Bs. ${bs.format(v)}`} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex-1 space-y-1.5 max-h-40 overflow-y-auto">
                      {summary.by_category.map((c, i) => (
                        <div key={c.category} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} /> {c.category}</span>
                          <span className="font-mono2 text-xs text-slate-500">${usd.format(c.usd)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : <p className="text-sm text-slate-400 py-8 text-center">Sin gastos este mes.</p>}
              </div>

              {/* By scope */}
              <div className={`${CARD} p-5`}>
                <h3 className="font-heading font-medium text-base mb-3">Gastos por ámbito</h3>
                {summary?.by_scope?.length ? (
                  <div className="space-y-3 pt-2">
                    {summary.by_scope.map((s, i) => {
                      const max = summary.by_scope[0].ves || 1;
                      return (
                        <div key={s.scope}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span>{s.scope}</span>
                            <span className="font-mono2 text-xs text-slate-500">Bs. {bs.format(s.ves)} · ${usd.format(s.usd)}</span>
                          </div>
                          <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${(s.ves / max) * 100}%`, background: PALETTE[i % PALETTE.length] }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : <p className="text-sm text-slate-400 py-8 text-center">Sin gastos este mes.</p>}
              </div>
            </div>

            {/* Budgets */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-medium text-lg flex items-center gap-2"><Target size={18} className="text-indigo-500" /> Presupuestos</h3>
              <button onClick={() => setBudgetModal(true)} className={`${BTN_GHOST} text-xs`} data-testid="add-budget-button"><Plus size={14} /> Presupuesto</button>
            </div>
            {summary?.budgets?.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8" data-testid="budgets-list">
                {summary.budgets.map((b) => (
                  <div key={b.id} className={`${CARD} p-4`} data-testid={`budget-${b.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-body font-medium text-sm">{b.category} <span className="text-slate-400 font-mono2 text-xs">· {b.scope}</span></p>
                        <p className="font-mono2 text-[11px] text-slate-400">Límite: {CUR_SYMBOL[b.currency] || b.currency} {usd.format(b.limit)} · Gastado: ${usd.format(b.spent_usd)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {b.exceeded && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-500" data-testid="budget-alert"><AlertTriangle size={12} /> Excedido</span>}
                        <button onClick={() => deleteBudget(b.id)} className="text-slate-300 hover:text-rose-500"><Trash2 size={14} /></button>
                      </div>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${b.exceeded ? "bg-rose-500" : b.percent > 80 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(b.percent, 100)}%` }} />
                    </div>
                    <p className="font-mono2 text-[10px] text-slate-400 mt-1 text-right">{b.percent}%</p>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400 mb-8">Aún no tienes presupuestos. Crea uno para controlar tus gastos por categoría.</p>}

            {/* Transactions */}
            <h3 className="font-heading font-medium text-lg mb-3">Movimientos ({txs.length})</h3>
            <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-xl" data-testid="tx-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-900 text-left font-mono2 text-[11px] text-slate-400 uppercase tracking-wider">
                    <th className="px-4 py-3">Fecha</th>
                    <th className="px-4 py-3">Concepto</th>
                    <th className="px-4 py-3">Ámbito</th>
                    <th className="px-4 py-3 text-right">Monto</th>
                    <th className="px-4 py-3 text-right">En Bs.</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {txs.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No hay movimientos este mes.</td></tr>
                  ) : txs.map((tx) => (
                    <tr key={tx.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/50" data-testid={`tx-row-${tx.id}`}>
                      <td className="px-4 py-3 font-mono2 text-xs text-slate-400">{tx.date}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-2 ${tx.kind === "income" ? "bg-emerald-500" : "bg-rose-500"}`} />
                        {tx.category}{tx.note ? <span className="text-slate-400"> · {tx.note}</span> : ""}
                      </td>
                      <td className="px-4 py-3 text-slate-500">{tx.scope}</td>
                      <td className={`px-4 py-3 text-right font-mono2 ${tx.kind === "income" ? "text-emerald-600 dark:text-emerald-400" : "text-slate-700 dark:text-slate-200"}`}>
                        {tx.kind === "income" ? "+" : "−"}{CUR_SYMBOL[tx.currency] || tx.currency} {usd.format(tx.amount)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono2 text-xs text-slate-400">Bs. {bs.format(tx.amount_ves)}</td>
                      <td className="px-4 py-3 text-right"><button onClick={() => deleteTx(tx.id)} className="text-slate-300 hover:text-rose-500" data-testid={`tx-delete-${tx.id}`}><Trash2 size={15} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {txModal && <TxModal meta={meta} onScopesUpdated={(s) => setMeta((m) => ({ ...m, scopes: s }))} onClose={() => setTxModal(false)} onSaved={() => { setTxModal(false); loadData(); }} />}
      {budgetModal && <BudgetModal meta={meta} onClose={() => setBudgetModal(false)} onSaved={() => { setBudgetModal(false); loadData(); }} />}
      {scopeModal && <ScopeManagerModal meta={meta} saveScopes={saveScopes} onClose={() => setScopeModal(false)} />}
    </div>
  );
}

function SummaryCard({ label, Icon, color, tone, data, thirdSym, testid }) {
  return (
    <div className={`${CARD} p-5`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}><Icon size={18} /></div>
        <span className="font-mono2 text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`font-heading font-light text-3xl tracking-tight ${tone}`} data-testid={`${testid}-bs`}>Bs. {bs.format(data?.ves || 0)}</p>
      <div className="flex items-center gap-3 mt-1 font-mono2 text-xs text-slate-400">
        <span data-testid={`${testid}-usd`}>$ {usd.format(data?.usd || 0)}</span>
        <span>·</span>
        <span data-testid={`${testid}-third`}>{thirdSym} {usd.format(data?.third || 0)}</span>
      </div>
    </div>
  );
}

const MODAL_WRAP = "fixed inset-0 z-[75] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4";
const MODAL_BOX = "w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl";

function TxModal({ meta, onScopesUpdated, onClose, onSaved }) {
  const [kind, setKind] = useState("expense");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("VES");
  const [scope, setScope] = useState(meta?.scopes?.[0] || "Personal");
  const [category, setCategory] = useState("");
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const cats = kind === "income" ? meta?.income_categories : meta?.expense_categories;

  const onScopeSelect = async (e) => {
    const v = e.target.value;
    if (v !== "__new__") { setScope(v); return; }
    const name = window.prompt("Nombre del nuevo ámbito (ej: Vehículo, Viajes)");
    if (!name || !name.trim()) return;
    try {
      const { data } = await api.put("/finance/settings", { scopes: [...(meta?.scopes || []), name.trim()] });
      onScopesUpdated?.(data.scopes);
      setScope(name.trim());
      toast.success("Ámbito creado");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const save = async () => {
    const amt = parseFloat(String(amount).replace(",", "."));
    if (isNaN(amt) || amt <= 0) { toast.error("Ingresa un monto válido"); return; }
    setSaving(true);
    try {
      await api.post("/finance/transactions", { kind, amount: amt, currency, scope, category: category || (cats?.[0] || "Otros"), date, note });
      toast.success("Movimiento guardado");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className={MODAL_WRAP} onClick={onClose} data-testid="tx-modal">
      <div className={MODAL_BOX} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-medium text-lg">Nuevo movimiento</h3>
          <button onClick={onClose} className={ICON_BTN}><X size={16} /></button>
        </div>
        <div className="grid grid-cols-2 gap-1 bg-slate-100 dark:bg-slate-800/60 rounded-xl p-1 mb-4">
          <button onClick={() => { setKind("expense"); setCategory(""); }} className={`py-2 rounded-lg text-sm font-medium transition-colors ${kind === "expense" ? "bg-rose-500 text-white" : "text-slate-500"}`} data-testid="tx-kind-expense">Gasto</button>
          <button onClick={() => { setKind("income"); setCategory(""); }} className={`py-2 rounded-lg text-sm font-medium transition-colors ${kind === "income" ? "bg-emerald-600 text-white" : "text-slate-500"}`} data-testid="tx-kind-income">Ingreso</button>
        </div>
        <div className="space-y-3">
          <div className="flex gap-2">
            <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Monto" inputMode="decimal" className={`${INPUT} flex-1`} data-testid="tx-amount" autoFocus />
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={`${INPUT} w-28`} data-testid="tx-currency">
              {meta?.currencies?.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <select value={scope} onChange={onScopeSelect} className={`${INPUT} flex-1`} data-testid="tx-scope">
              {meta?.scopes?.map((s) => <option key={s} value={s}>{s}</option>)}
              <option value="__new__">➕ Nuevo ámbito…</option>
            </select>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={`${INPUT} flex-1`} data-testid="tx-category">
              <option value="">Categoría...</option>
              {cats?.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={INPUT} data-testid="tx-date" />
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota (opcional)" className={INPUT} data-testid="tx-note" />
        </div>
        <button onClick={save} disabled={saving} className={`${BTN_PRIMARY} w-full mt-5 justify-center`} data-testid="tx-save">{saving ? "Guardando..." : "Guardar"}</button>
      </div>
    </div>
  );
}

function BudgetModal({ meta, onClose, onSaved }) {
  const [scope, setScope] = useState(meta?.scopes?.[0] || "Personal");
  const [category, setCategory] = useState(meta?.expense_categories?.[0] || "Otros");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const amt = parseFloat(String(amount).replace(",", "."));
    if (isNaN(amt) || amt <= 0) { toast.error("Ingresa un monto válido"); return; }
    setSaving(true);
    try {
      await api.post("/finance/budgets", { scope, category, amount: amt, currency });
      toast.success("Presupuesto guardado");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className={MODAL_WRAP} onClick={onClose} data-testid="budget-modal">
      <div className={MODAL_BOX} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-medium text-lg flex items-center gap-2"><Target size={18} className="text-indigo-500" /> Presupuesto mensual</h3>
          <button onClick={onClose} className={ICON_BTN}><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <select value={scope} onChange={(e) => setScope(e.target.value)} className={INPUT} data-testid="budget-scope">
            {meta?.scopes?.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={INPUT} data-testid="budget-category">
            {meta?.expense_categories?.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="flex gap-2">
            <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Límite mensual" inputMode="decimal" className={`${INPUT} flex-1`} data-testid="budget-amount" autoFocus />
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={`${INPUT} w-28`} data-testid="budget-currency">
              {["USD", "VES", "EUR", "USDT", "USDC"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <button onClick={save} disabled={saving} className={`${BTN_PRIMARY} w-full mt-5 justify-center`} data-testid="budget-save">{saving ? "Guardando..." : "Guardar"}</button>
      </div>
    </div>
  );
}

function ScopeManagerModal({ meta, saveScopes, onClose }) {
  const [scopes, setScopes] = useState(meta?.scopes || []);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const persist = async (next) => {
    setBusy(true);
    const result = await saveScopes(next);
    setBusy(false);
    if (result) { setScopes(result); return true; }
    return false;
  };

  const add = async () => {
    const v = name.trim();
    if (!v) return;
    if (scopes.some((s) => s.toLowerCase() === v.toLowerCase())) { toast.info("Ese ámbito ya existe"); return; }
    const ok = await persist([...scopes, v]);
    if (ok) { setName(""); toast.success("Ámbito añadido"); }
  };

  const remove = async (s) => {
    if (scopes.length <= 1) { toast.error("Debe quedar al menos un ámbito"); return; }
    if (!window.confirm(`¿Eliminar el ámbito "${s}"? Los movimientos existentes conservarán su ámbito, pero ya no aparecerá para elegir.`)) return;
    const ok = await persist(scopes.filter((x) => x !== s));
    if (ok) toast.success("Ámbito eliminado");
  };

  return (
    <div className={MODAL_WRAP} onClick={onClose} data-testid="scope-modal">
      <div className={MODAL_BOX} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-medium text-lg flex items-center gap-2"><Settings2 size={18} className="text-indigo-500" /> Gestionar ámbitos</h3>
          <button onClick={onClose} className={ICON_BTN} data-testid="scope-modal-close"><X size={16} /></button>
        </div>
        <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-4">Personaliza los ámbitos para clasificar tus movimientos (ej: Casa, Negocio, Vehículo, Viajes).</p>
        <div className="flex gap-2 mb-4">
          <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Nuevo ámbito…" className={`${INPUT} flex-1`} data-testid="scope-new-input" autoFocus />
          <button onClick={add} disabled={busy || !name.trim()} className={BTN_PRIMARY} data-testid="scope-add-button"><Plus size={16} /> Añadir</button>
        </div>
        <div className="flex flex-col gap-1.5 max-h-64 overflow-y-auto" data-testid="scope-list">
          {scopes.map((s) => (
            <div key={s} className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800" data-testid={`scope-item-${s}`}>
              <span className="flex items-center gap-2.5 text-sm text-slate-700 dark:text-slate-200 min-w-0"><Tag size={15} className="text-indigo-500 shrink-0" /> <span className="truncate">{s}</span></span>
              <button onClick={() => remove(s)} disabled={busy} className="text-slate-300 hover:text-rose-500 transition-colors shrink-0" title="Eliminar" data-testid={`scope-delete-${s}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
